import React, { useMemo } from "react";

import { Grid } from "@mui/material";
import { format } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { useTranslation } from "react-i18next";

import { User } from "../../../../../models/user";
import Input from "../../../../Form/Input";
import PhoneInput from "../../../../Form/PhoneInput";
import SkeletonComponent from "../../../Skeleton";
import { AvatarGeneric } from "../AvatarGeneric";
import useStyles from "./styles";

const ALLOWED_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif"];

interface Props {
  isOnProfilePage?: boolean;
  showingFromPeopleSettings: boolean;
  registeringUser?: boolean;
  view: boolean;
  isLoading: boolean;
  userForm: User;
  getErrorByField: (value: string) => string | undefined;
  updateOrCreate: (attribute: string, value: any) => void;
}

const ContactDetailsComponent = ({
  isOnProfilePage = false,
  showingFromPeopleSettings,
  registeringUser,
  view,
  isLoading,
  userForm,
  getErrorByField,
  updateOrCreate,
}: Props) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const SIZE_PROFILE = useMemo(() => (isOnProfilePage ? 3 : 4), [isOnProfilePage]);

  return (
    <Grid
      container
      direction="row"
      alignItems="flex-start"
      spacing={2}
      className={classes.contactContainer}
    >
      <Grid
        item
        container
        direction={"row"}
        alignItems="flex-start"
        sm={12}
        md={registeringUser || (registeringUser === undefined && !isOnProfilePage) ? 10 : 12}
        lg={registeringUser || (registeringUser === undefined && !isOnProfilePage) ? 10 : 12}
        spacing={2}
      >
        <Grid item xs={12} sm={6} md={SIZE_PROFILE} lg={SIZE_PROFILE} className={classes.mb16}>
          <SkeletonComponent isLoading={isLoading}>
            <Input
              disabled={view}
              label={translate("firstName")}
              name="first_name"
              data-cy="firstName"
              placeholder={translate("firstName")}
              value={userForm?.name || ""}
              error={!!getErrorByField("name")}
              errorMessage={getErrorByField("name")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateOrCreate("name", e.target.value)
              }
            />
          </SkeletonComponent>
        </Grid>
        <Grid item xs={12} sm={6} md={SIZE_PROFILE} lg={SIZE_PROFILE}>
          <SkeletonComponent isLoading={isLoading}>
            <Input
              disabled={view}
              label={translate("lastName")}
              name="last_name"
              data-cy="lastName"
              placeholder={translate("lastName")}
              value={userForm?.lastName || ""}
              error={!!getErrorByField("lastName")}
              errorMessage={getErrorByField("lastName")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateOrCreate("lastName", e.target.value)
              }
            />
          </SkeletonComponent>
        </Grid>
        {showingFromPeopleSettings && <Grid className={classes.xsHidden} item md={4} lg={4}></Grid>}

        <Grid item xs={12} sm={SIZE_PROFILE} md={SIZE_PROFILE} lg={SIZE_PROFILE}>
          <SkeletonComponent isLoading={isLoading}>
            <PhoneInput
              disabled={view}
              data-cy="phoneNumber"
              placeholder="61430111900"
              international
              defaultCountry="AU"
              name="phone"
              label={translate("mobile")}
              value={userForm?.phone || ""}
              error={!!getErrorByField("phone")}
              errorMessage={getErrorByField("phone")}
              onChange={(value) => {
                updateOrCreate("phone", value || "");
              }}
            />
          </SkeletonComponent>
        </Grid>
        {(registeringUser === undefined || registeringUser) && (
          <Grid item xs={12} sm={SIZE_PROFILE} md={SIZE_PROFILE} lg={SIZE_PROFILE}>
            <SkeletonComponent isLoading={isLoading}>
              <Input
                disabled={view}
                label={translate("dateOfBirth")}
                name="date_of_birth"
                data-cy="dateOfBirth"
                type="date"
                min="1900-01-01"
                error={!!getErrorByField("dateOfBirth")}
                errorMessage={getErrorByField("dateOfBirth")}
                value={
                  userForm?.dateOfBirth
                    ? format(
                        utcToZonedTime(
                          userForm?.dateOfBirth,
                          Intl.DateTimeFormat().resolvedOptions().timeZone
                        ),
                        "yyyy-MM-dd"
                      )
                    : ""
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  updateOrCreate(
                    "dateOfBirth",
                    zonedTimeToUtc(
                      event.target.value,
                      Intl.DateTimeFormat().resolvedOptions().timeZone
                    ).toISOString()
                  )
                }
              />
            </SkeletonComponent>
          </Grid>
        )}
        <Grid
          item
          xs={12}
          sm={4}
          md={registeringUser || registeringUser === undefined ? SIZE_PROFILE : 6}
          lg={registeringUser || registeringUser === undefined ? SIZE_PROFILE : 6}
        >
          <SkeletonComponent isLoading={isLoading}>
            <Input
              disabled={view}
              label={translate("email")}
              name="email"
              data-cy="email"
              placeholder={translate("email")}
              error={!!getErrorByField("email")}
              errorMessage={getErrorByField("email")}
              value={userForm?.email || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateOrCreate("email", e.target.value)
              }
            />
          </SkeletonComponent>
        </Grid>
        {!showingFromPeopleSettings && !isOnProfilePage && (
          <Grid
            item
            md={registeringUser || registeringUser === undefined ? SIZE_PROFILE : 6}
            lg={registeringUser || registeringUser === undefined ? SIZE_PROFILE : 6}
          >
            <SkeletonComponent isLoading={isLoading}>
              <Input
                disabled={view}
                label={translate("position")}
                name="position"
                data-cy="position"
                placeholder="Developer"
                error={!!getErrorByField("position")}
                errorMessage={getErrorByField("position")}
                value={userForm?.position || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateOrCreate("position", e.target.value)
                }
              />
            </SkeletonComponent>
          </Grid>
        )}
      </Grid>

      {(showingFromPeopleSettings || registeringUser === undefined || registeringUser) &&
        !isOnProfilePage && (
          <Grid item xs={12} sm={12} md={2} lg={2}>
            <SkeletonComponent isLoading={isLoading} classesName={classes.avatarSkeleton}>
              <AvatarGeneric
                allowedImageExtensions={ALLOWED_IMAGE_EXTENSIONS}
                name="picture"
                data-cy="avatar"
                isOnViewMode={view}
                picture={userForm?.picture?.mediaLink || ""}
                uploadMode="s3"
                onChangePicture={(value) => {
                  const hasExistingPicture = !!userForm?.picture?.mediaLink;
                  updateOrCreate(
                    "picture",
                    value
                      ? hasExistingPicture
                        ? {
                            mediaLink: value,
                            id: userForm?.picture?.id,
                            name: userForm?.picture?.name,
                          }
                        : { mediaLink: value }
                      : undefined
                  );
                }}
              />
            </SkeletonComponent>
          </Grid>
        )}
    </Grid>
  );
};

export default ContactDetailsComponent;
